import React from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { Medication } from '../types';

interface HasAdditionalAttackMedicationProps {
  additionalAttackMedications: Medication[];
  setAdditionalAttackMedications: (headaches: Medication[]) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

const HasAdditionalAttackMedication: React.FC<
  HasAdditionalAttackMedicationProps
> = ({
  additionalAttackMedications,
  setAdditionalAttackMedications,
  handleFormSubmit,
}) => {
  const handleInputChange = (
    index: number,
    field: keyof Medication,
    value: string
  ) => {
    const updatedMedications = [...additionalAttackMedications];
    updatedMedications[index][field] = value;
    setAdditionalAttackMedications(updatedMedications);
  };

  const handleAddNewSet = () => {
    setAdditionalAttackMedications([
      ...additionalAttackMedications,
      { name: '', dose: '', effect: '', frequency: '', effectiveness: '' },
    ]);
  };

  return (
    <Box>
      {additionalAttackMedications.map((medication, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to and edited in place
        <Box key={index}>
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilket (läkemedelsnamn)?
          </Typography>
          <input
            type="text"
            onBlur={handleFormSubmit}
            value={medication.name}
            onChange={(e) => handleInputChange(index, 'name', e.target.value)}
            placeholder="Läkemedelsnamn"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
              marginBottom: '16px',
            }}
          />
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilken dos (styrka/ behandlingstillfälle)?
          </Typography>
          <input
            type="text"
            onBlur={handleFormSubmit}
            value={medication.dose}
            onChange={(e) => handleInputChange(index, 'dose', e.target.value)}
            placeholder="Styrka"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
              marginBottom: '16px',
            }}
          />
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilken symptomlindrande effekt har detta läkemedel?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {['Mycket god', 'God', 'Måttlig', 'Sparsam', 'Ingen'].map(
              (effect) => (
                <Box
                  component="label"
                  key={effect}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`additionalPainMedicationEffect${index}`}
                    value={effect}
                    checked={medication.effect === effect}
                    onChange={() => handleInputChange(index, 'effect', effect)}
                    style={{ marginRight: '8px' }}
                  />
                  {effect}
                </Box>
              )
            )}
          </Box>
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur ofta hjälper detta läkemedel?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {['Alltid', 'För det mesta', 'Ibland', 'Sällan', 'Aldrig'].map(
              (effectiveness) => (
                <Box
                  component="label"
                  key={effectiveness}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`additionalPainMedicationEffectiveness${index}`}
                    value={effectiveness}
                    checked={medication.effectiveness === effectiveness}
                    onChange={() =>
                      handleInputChange(index, 'effectiveness', effectiveness)
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {effectiveness}
                </Box>
              )
            )}
          </Box>
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur ofta tar du detta läkemedel?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
            {[
              'mindre än 1 dag i veckan',
              '1-2 dagar i veckan',
              '3-5 dagar i veckan',
              'mer än 5 dagar i veckan',
            ].map((frequency) => (
              <Box
                component="label"
                key={frequency}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`additionalPainMedicationFrequency${index}`}
                  value={frequency}
                  checked={medication.frequency === frequency}
                  onChange={() =>
                    handleInputChange(index, 'frequency', frequency)
                  }
                  style={{ marginRight: '8px' }}
                />
                {frequency}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
      <Box
        component="button"
        onClick={handleAddNewSet}
        sx={{
          mt: 2,
          py: 1.5,
          px: 2,
          backgroundColor: DS.deepTeal,
          color: '#FFFFFF',
          borderRadius: '6px',
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          fontFamily: DS.fontBody,
          fontSize: '16px',
        }}
      >
        Lägg till ytterligare anfallsmedicin
      </Box>
    </Box>
  );
};

export default HasAdditionalAttackMedication;
